import React from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import useFitnessVillageCopy from '../../../../../hooks/useFitnessVillageCopy';
import { useLoginForm } from '../../hooks/useLoginForm';
import { IFormUIProps } from '../../Types';
import { SurfaceProp } from '../../../../shared/dfcVillage/style';
import {
  Field,
  FieldAdornment,
  FieldLabel,
  FieldWrap,
  ForgotButton,
  Form,
  Spinner,
  SubmitButton,
} from './style';

type Props = Pick<IFormUIProps, 'form' | 'onSubmit' | 'isLoading'> & SurfaceProp;

/** Email + password sign-in — the same fields and validation as every other theme, reskinned. */
const LoginForm: React.FC<Props> = ({ form, onSubmit, isLoading, surface }) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = form;
  const copy = useFitnessVillageCopy().auth;
  const { handleResetPassword, isResetButtonDisabled, showPassword, setShowPassword } =
    useLoginForm(form);

  return (
    <Form onSubmit={handleSubmit(onSubmit)} noValidate>
      <FieldWrap>
        <FieldLabel surface={surface} htmlFor="dfc-auth-email">
          {copy.email}
        </FieldLabel>
        <Field
          surface={surface}
          hasError={!!errors.email}
          id="dfc-auth-email"
          dir="ltr"
          type="email"
          autoComplete="email"
          placeholder={copy.emailPlaceholder}
          aria-invalid={!!errors.email}
          data-testid="email-input"
          {...register('email')}
        />
      </FieldWrap>

      <FieldWrap>
        <FieldLabel surface={surface} htmlFor="dfc-auth-password">
          {copy.password}
        </FieldLabel>
        <Field
          surface={surface}
          hasError={!!errors.password}
          id="dfc-auth-password"
          dir="ltr"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          placeholder="••••••••"
          aria-invalid={!!errors.password}
          data-testid="password-input"
          {...register('password')}
        />
        <FieldAdornment
          surface={surface}
          type="button"
          aria-label={showPassword ? copy.hidePassword : copy.showPassword}
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </FieldAdornment>
      </FieldWrap>

      <ForgotButton
        surface={surface}
        type="button"
        disabled={isResetButtonDisabled}
        onClick={handleSubmit(handleResetPassword)}
        data-testid="forgot-password-button"
      >
        {isResetButtonDisabled ? copy.resetSent : copy.forgotPassword}
      </ForgotButton>

      <SubmitButton
        surface={surface}
        type="submit"
        disabled={isLoading}
        aria-busy={isLoading}
        data-testid="submit-button"
      >
        {isLoading ? <Spinner surface={surface} /> : copy.signIn.cta}
      </SubmitButton>
    </Form>
  );
};

export default LoginForm;
